import React, { useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import { supabase } from '../../config/supabase';
import agent from '../../agent';
import MusicPicker from './MusicPicker';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const MAX_MB = 50;
const BUCKET = 'reels';

async function extractThumbnail(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    const objUrl = URL.createObjectURL(file);
    video.src = objUrl;

    const cleanup = () => URL.revokeObjectURL(objUrl);

    video.onloadedmetadata = () => {
      video.currentTime = video.duration >= 1 ? 1 : 0;
    };

    video.onseeked = () => {
      canvas.width = video.videoWidth || 360;
      canvas.height = video.videoHeight || 640;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      cleanup();
      canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
    };

    video.onerror = () => { cleanup(); resolve(null); };
  });
}

async function uploadToSupabase(path, fileOrBlob, contentType) {
  const { error } = await supabase.storage.from(BUCKET).upload(path, fileOrBlob, {
    contentType,
    upsert: false,
  });
  if (error) throw new Error(error.message);
  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return publicUrl;
}

const BlastUploadDialog = ({ open, onClose, onSuccess, freakId, freakPlaceId }) => {
  const fileInputRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [music, setMusic] = useState(null);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setCaption('');
    setFile(null);
    setPreviewUrl(null);
    setProgress(0);
    setStatusText('');
    setError('');
    setUploading(false);
    setMusic(null);
  };

  const handleClose = () => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleFilePick = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    const sizeMB = picked.size / (1024 * 1024);
    if (sizeMB > MAX_MB) {
      setError(`File is ${sizeMB.toFixed(1)} MB — maximum is ${MAX_MB} MB.`);
      e.target.value = '';
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);

    try {
      setStatusText('Extracting thumbnail…');
      const thumbBlob = await extractThumbnail(file);
      setProgress(0.1);

      const ts = Date.now();

      setStatusText('Uploading video…');
      const videoUrl = await uploadToSupabase(`videos/${ts}.mp4`, file, 'video/mp4');
      setProgress(0.7);

      let thumbnailUrl;
      if (thumbBlob) {
        setStatusText('Uploading thumbnail…');
        try {
          thumbnailUrl = await uploadToSupabase(`thumbnails/${ts}.jpg`, thumbBlob, 'image/jpeg');
        } catch {}
      }
      setProgress(0.9);

      setStatusText('Saving…');
      await agent.Reels.create({
        videoUrl,
        thumbnailUrl,
        caption: caption.trim() || undefined,
        freakId,
        freakPlaceId,
        ...(music ? {
          musicTrackId: music.trackId,
          musicUrl: music.url,
          musicName: music.name,
          musicArtist: music.artist,
          musicStartMs: music.startMs,
          musicLicenseUrl: music.licenseUrl,
        } : {}),
      });

      setProgress(1);
      setStatusText('Done!');
      setTimeout(() => {
        reset();
        onClose();
        onSuccess?.();
      }, 600);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
      setProgress(0);
      setStatusText('');
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d2a1e 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <LocalFireDepartmentIcon sx={{ color: DEFAULT_THEME_COLOR }} />
        Add Blast
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Add a caption (optional)"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          inputProps={{ maxLength: 300 }}
          disabled={uploading}
          sx={{
            mb: 2,
            '& .MuiInputBase-root': { color: 'white', background: 'rgba(255,255,255,0.05)', borderRadius: 2 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
            '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: DEFAULT_THEME_COLOR },
          }}
        />

        <MusicPicker onSelect={setMusic} />
        {music && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 2, px: 1.5, py: 0.75 }}>
            <LocalFireDepartmentIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 18 }} />
            <Typography sx={{ color: 'white', fontSize: '0.8rem', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {music.name} — {music.artist}
            </Typography>
            <Button size="small" onClick={() => setMusic(null)} sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 0 }}>Remove</Button>
          </Box>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/mov,video/*"
          style={{ display: 'none' }}
          onChange={handleFilePick}
        />
        <Button
          variant="outlined"
          startIcon={<VideoFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          fullWidth
          sx={{
            mb: 2,
            color: file ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.55)',
            borderColor: file ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.18)',
            borderRadius: 2,
            textTransform: 'none',
            justifyContent: 'flex-start',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover': { borderColor: DEFAULT_THEME_COLOR, color: DEFAULT_THEME_COLOR },
          }}
        >
          {file ? file.name : 'Choose video (max 50 MB)'}
        </Button>

        {previewUrl && (
          <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', background: '#000', maxHeight: 300, display: 'flex', justifyContent: 'center' }}>
            <video
              key={previewUrl}
              src={previewUrl}
              controls
              muted
              playsInline
              style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
            />
          </Box>
        )}

        {uploading && (
          <Box sx={{ mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress * 100}
              sx={{
                borderRadius: 2,
                height: 6,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${DEFAULT_THEME_COLOR}, #00e5ff)`,
                  borderRadius: 2,
                },
              }}
            />
            <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.78rem', mt: 0.75, textAlign: 'center' }}>
              {statusText}
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.35)', borderRadius: 2, px: 2, py: 1 }}>
            <Typography sx={{ color: '#f43f5e', fontSize: '0.85rem' }}>{error}</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={uploading}
          sx={{ color: 'rgba(255,255,255,0.45)', '&:focus': { outline: 'none' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file || uploading}
          sx={{
            background: `linear-gradient(90deg, ${DEFAULT_THEME_COLOR}, #00e5ff)`,
            color: '#0d1b2a',
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
            '&:disabled': { opacity: 0.4 },
            '&:focus': { outline: 'none' },
          }}
        >
          {uploading ? 'Uploading…' : 'Upload Blast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlastUploadDialog;
